import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Site-wide header strip showing NIFTY 50 + SENSEX from the NSE Bhavcopy
 * indices archive (see /app/backend/eod_sync.py::fetch_and_store_nse_indices).
 *
 * After the EOD pivot we no longer poll every 30 s — the values come from a
 * once-a-day archive file, so a single fetch on mount is enough. The strip
 * also now shows the trade_date the figures are for ("as of 28 May 2026")
 * so users see at-a-glance which day's close they're looking at.
 *
 * Hidden silently if the endpoint fails or returns no data (so the site
 * header never looks broken on first paint).
 */
export const MarketTicker = () => {
  const [indices, setIndices] = useState(null);
  const [tradeDate, setTradeDate] = useState(null);
  // ``fetchDone`` lets us distinguish "still loading on first paint" from
  // "fetch completed and returned no data". The latter happens on a fresh
  // production deploy that hasn't yet run the EOD sync — we hide the strip
  // entirely instead of leaving it stuck on "Loading market data…".
  const [fetchDone, setFetchDone] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/market/indices`, { timeout: 8000 });
        if (!mountedRef.current) return;
        if (data?.data && Object.keys(data.data).length > 0) {
          setIndices(data.data);
          setTradeDate(data.trade_date || null);
        }
      } catch {
        /* ticker is decorative; silence network blips */
      } finally {
        if (mountedRef.current) setFetchDone(true);
      }
    })();
    return () => { mountedRef.current = false; };
  }, []);

  // First-paint guard: show the "Loading…" placeholder for one network
  // round-trip. Once the fetch completes with no data (e.g. a fresh prod
  // deploy that hasn't run the EOD sync yet) we keep the 32px shell — the
  // mobile header docks below it (`top-[32px]` in Layout.js) — but swap
  // the misleading "Loading market data…" copy for a quiet "—" so the
  // ticker doesn't look broken.
  const placeholder = !indices;
  const emptyAfterFetch = fetchDone && !indices;
  const rows = indices ? [indices.nifty, indices.sensex].filter(Boolean) : [];
  // Format the trade_date (YYYY-MM-DD) for display: "28 May 2026".
  // Falls back gracefully if the backend hasn't synced yet.
  const fmtTradeDate = (iso) => {
    if (!iso) return '';
    try {
      const [y, m, d] = iso.split('-').map(Number);
      const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
      return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return iso; }
  };

  return (
    <div
      data-testid="market-ticker"
      className="w-full text-sm sticky top-0 z-40"
      style={{
        backgroundColor: '#1B4332',
        color: '#F7F7F5',
        borderBottom: '1px solid #0E2A20',
      }}
    >
      <div className="max-w-[1600px] mx-auto relative flex items-center justify-center gap-2 md:gap-4 px-3 md:px-4 h-8">
        {placeholder ? (
          <div className="text-[11px] md:text-xs" style={{ color: '#A9C8B6', display: 'flex', alignItems: 'center' }}>
            {emptyAfterFetch ? 'NSE EOD prices · sync pending' : 'Loading market data…'}
          </div>
        ) : (
        <div className="flex items-center gap-3 md:gap-6 overflow-x-auto text-xs md:text-sm flex-1 justify-around md:justify-center">
          {rows.map((ix) => {
            const up = (ix.change ?? 0) >= 0;
            const Icon = up ? TrendingUp : TrendingDown;
            return (
              <div
                key={ix.name}
                className="flex items-center gap-1 md:gap-2 whitespace-nowrap"
                data-testid={`ticker-${ix.name.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <span className="text-[10px] md:text-xs uppercase font-medium" style={{ letterSpacing: '0.06em', color: '#A9C8B6' }}>
                  {ix.name}
                </span>
                <span className="font-medium tabular-nums">
                  {Number(ix.price).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span
                  className="inline-flex items-center gap-0.5 text-[10px] md:text-xs font-medium"
                  style={{ color: up ? '#A8E6C1' : '#E7A69A' }}
                >
                  <Icon className="h-3 w-3" style={{ strokeWidth: 2 }} />
                  <span className="hidden md:inline">{up ? '+' : ''}{Number(ix.change).toFixed(2)} </span>
                  ({up ? '+' : ''}{Number(ix.change_pct).toFixed(2)}%)
                </span>
              </div>
            );
          })}
        </div>
        )}
        {/* Trade-date chip: ALWAYS visible (was previously ``hidden md:inline``
            + absolute-positioned, so it disappeared on mobile and got clipped
            on narrow desktops). The user explicitly asked for the close date
            to be on screen everywhere so it's never confused with a live
            price. Compact "27-May-26" on phones, full "Close · 27 May 2026"
            from md upwards. */}
        {!placeholder && tradeDate && (
          <span
            className="inline-flex items-center text-[10px] md:text-[11px] px-1.5 md:px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0"
            style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: '#A9C8B6' }}
            data-testid="market-ticker-status"
            title="Closing prices shown — sourced from NSE Bhavcopy archive"
          >
            <span className="hidden md:inline">Close ·&nbsp;</span>{fmtTradeDate(tradeDate)}
          </span>
        )}
      </div>
    </div>
  );
};

export default MarketTicker;
